import { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  TextField,
  Alert,
  Snackbar,
  Autocomplete,
  Grid,
  Button,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { WorkerLedger as WorkerLedgerType } from '../../types/payment';
import { ActiveWorker } from '../../types/workerAssignment';
import api from '../../services/api';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import PrintIcon from '@mui/icons-material/Print';

export default function WorkerLedger({ initialWorkerId, initialStartDate, initialEndDate }: { initialWorkerId?: string, initialStartDate?: string, initialEndDate?: string }) {
  // Calculate default start date as 1st of last month
  const getDefaultStartDate = () => {
    const now = new Date();
    // Get last month (0-11)
    let month = now.getMonth() - 1;
    let year = now.getFullYear();
    
    // Handle January (month 0) -> go to December of previous year
    if (month < 0) {
      month = 11;
      year -= 1;
    }
    
    // Format as YYYY-MM-DD (always 1st day of last month)
    const monthStr = String(month + 1).padStart(2, '0');
    return `${year}-${monthStr}-01`;
  };

  const [activeWorkers, setActiveWorkers] = useState<ActiveWorker[]>([]);
  const [selectedWorker, setSelectedWorker] = useState<ActiveWorker | null>(null);
  const [startDate, setStartDate] = useState<string>(initialStartDate || getDefaultStartDate());
  const [endDate, setEndDate] = useState<string>(initialEndDate || new Date().toISOString().split('T')[0]);
  const [ledger, setLedger] = useState<WorkerLedgerType | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const columns: GridColDef[] = [
    { 
      field: 'date', 
      headerName: 'Date', 
      width: 130
    },
    { field: 'description', headerName: 'Description', width: 300 },
    { 
      field: 'debit', 
      headerName: 'Debit', 
      width: 130
    },
    { 
      field: 'credit', 
      headerName: 'Credit', 
      width: 130
    },
    { 
      field: 'balance', 
      headerName: 'Balance', 
      width: 130
    },
    { field: 'reference', headerName: 'Reference', width: 200 },
  ];

  useEffect(() => {
    fetchWorkers();
  }, []);

  useEffect(() => {
    if (selectedWorker && startDate && endDate) {
      fetchLedger();
    }
  }, [selectedWorker, startDate, endDate]);

  // When workers load, preselect if initialWorkerId provided
  useEffect(() => {
    if (!initialWorkerId) return;
    const w = activeWorkers.find(w => w.worker_id === initialWorkerId);
    if (w) setSelectedWorker(w);
  }, [initialWorkerId, activeWorkers]);

  const fetchWorkers = async () => {
    try {
      const response = await api.get('/workers');
      setActiveWorkers(response.data.filter((worker: ActiveWorker) => worker.status === 'ACTIVE'));
    } catch (error) {
      console.error('Error fetching workers:', error);
      setError('Failed to fetch workers');
    }
  };

  const fetchLedger = async () => {
    if (!selectedWorker) return;

    setLoading(true);
    try {
      const response = await api.get(
        `/payments/worker-ledger/${selectedWorker.worker_id}?start_date=${startDate}&end_date=${endDate}`
      );
      setLedger(response.data);
    } catch (error) {
      console.error('Error fetching ledger:', error);
      setError('Failed to fetch ledger');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (!ledger) return;

    const doc = new jsPDF();

    // Title and worker info
    doc.setFontSize(16);
    doc.text('Worker Ledger', 14, 15);
    doc.setFontSize(12);
    doc.text(`Worker: ${ledger.worker_name} (${ledger.worker_id})`, 14, 25);
    doc.text(`Period: ${startDate} to ${endDate}`, 14, 32);
    doc.text(`Opening Balance: ₹${ledger.opening_balance.toFixed(2)}`, 14, 39);
    doc.text(`Closing Balance: ₹${ledger.closing_balance.toFixed(2)}`, 14, 46);

    // Table data
    const tableColumn = [
      'Date',
      'Description',
      'Debit',
      'Credit',
      'Balance',
      'Reference'
    ];
    const tableRows = ledger.entries.map(entry => [
      entry.date,
      entry.description,
      entry.debit !== null && entry.debit !== undefined ? entry.debit.toFixed(2) : '',
      entry.credit !== null && entry.credit !== undefined ? entry.credit.toFixed(2) : '',
      entry.balance !== null && entry.balance !== undefined ? entry.balance.toFixed(2) : '',
      entry.reference
    ]);

    (doc as any).autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 52,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [22, 160, 133] }
    });

    doc.save(`Worker_Ledger_${ledger.worker_name}_${startDate}_to_${endDate}.pdf`);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" sx={{ mb: 3 }}>Worker Ledger</Typography>

      <Button
        variant="contained"
        color="primary"
        startIcon={<PrintIcon />}
        sx={{ mb: 2 }}
        onClick={handlePrint}
        disabled={!ledger}
      >
        Print PDF
      </Button>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Autocomplete
              options={activeWorkers}
              getOptionLabel={(option) => `${option.worker_id} - ${option.name}`}
              value={selectedWorker}
              onChange={(_event, newValue) => setSelectedWorker(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Worker"
                  fullWidth
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Paper>

      {ledger && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1">
                Worker: {ledger.worker_name}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1">
                Opening Balance: ₹{ledger.opening_balance.toFixed(2)}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1">
                Closing Balance: ₹{ledger.closing_balance.toFixed(2)}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      <DataGrid
        autoHeight
        rows={ledger?.entries || []}
        columns={columns}
        loading={loading}
        pageSizeOptions={[5, 10, 25, 50]}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
          sorting: {
            sortModel: [{ field: 'date', sort: 'asc' }],
          },
        }}
      />

      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
} 